import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Box, Button, CircularProgress, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import { UIContext } from '../../../../../context/UIContext';
import { MobileRowExpandableContent, MobileRowMainContent } from './MobileRowContent';
import { SidekickDataGridConfigContext } from '../../../../../context/SidekickDataGridConfigContext';
import useBuildColumns from '../helpers/Columns';
import { SidekickTablesContext } from '../../helpers/SidekickTablesContext';
import { Product } from '../../helpers/types';
import DataTable from '../../sharedComponents/DataTable';
import MobileTable from '../../sharedComponents/MobileTable';
import TableWrapper from '../../sharedComponents/TableWrapper';
import GridColumns from '../../helpers/GridColumns';
import MobileRowStructure from '../../sharedComponents/MobileRowStructure';

const renderRow = (data: Product, currencySymbol: string) => (
  <MobileRowStructure
    mainContent={<MobileRowMainContent data={data} />}
    expandableContent={<MobileRowExpandableContent data={data} currencySymbol={currencySymbol} />}
  />
);

const ImportedProductsWrapper = ({
  isLoading,
  openDialog,
  currencySymbol,
}: {
  isLoading: boolean;
  openDialog?: () => void;
  currencySymbol: string;
}) => {
  const { t } = useTranslation();
  const { isMobile, isMediumDevice } = useContext(UIContext);
  const { isLoading: isAddingProducts, callApiFunction } = useContext(SidekickTablesContext);

  const { columnVisibilityModel, showColumnsButton } = useContext(SidekickDataGridConfigContext);

  const [resizeToken, setResizeToken] = useState<number>(0);

  const columns = useBuildColumns(currencySymbol);

  useEffect(() => {
    const handleResize = () => {
      setTimeout(() => setResizeToken((prevToken) => prevToken + 1), 100);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 1,
          gap: 1,
          ...(!isMediumDevice && {
            flexDirection: 'column',
            paddingTop: 1,
          }),
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            ...(isMobile && {
              paddingLeft: 1,
            }),
          }}
        >
          <Typography variant="h2">{t('screens.products.sidekick.import.products')}</Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'end',
            mb: 1,
            gap: 1,
            ...(isMobile && {
              justifyContent: 'start',
              flexDirection: 'column',
              marginX: 1,
            }),
          }}
        >
          {openDialog && (
            <Button
              color="primary"
              aria-label={t('screens.products.sidekick.import.uploadNewFile')}
              disableElevation
              data-qa-id="button-open-csv-dialog"
              variant="outlined"
              startIcon={<CloudUploadIcon />}
              onClick={openDialog}
            >
              {t('screens.products.sidekick.import.uploadNewFile')}
            </Button>
          )}
          <Button
            aria-label={
              isAddingProducts
                ? t('screens.products.sidekick.import.productsTable.adding')
                : t('screens.products.sidekick.import.productsTable.addAllProducts')
            }
            color="primary"
            data-qa-id="button-add-to-catalogue"
            variant="contained"
            disableElevation
            onClick={() => callApiFunction()}
          >
            {isAddingProducts && (
              <>
                <CircularProgress
                  aria-label="Adding products"
                  aria-live="polite"
                  aria-busy={isAddingProducts}
                  color="inherit"
                  size="20px"
                  sx={{ marginRight: 1 }}
                />
                {t('screens.products.sidekick.import.productsTable.adding')}
              </>
            )}
            {!isAddingProducts && t('screens.products.sidekick.import.productsTable.addAllProducts')}
          </Button>
          {showColumnsButton && !isMobile && <GridColumns />}
        </Box>
      </Box>

      <TableWrapper key={resizeToken}>
        {isMobile ? (
          <MobileTable<Product>
            isLoading={isLoading}
            renderRow={(data) => renderRow(data, currencySymbol)}
            emptyStateTitle="screens.products.sidekick.import.noProducts.title"
            emptyStateSubtitle="screens.products.sidekick.import.noProducts.subtitle"
          />
        ) : (
          <DataTable<Product>
            isLoading={isLoading}
            columns={columns}
            getRowId={(row) => row.id}
            dataQaId="table-products"
            columnVisibilityModel={columnVisibilityModel}
          />
        )}
      </TableWrapper>
    </>
  );
};

export default ImportedProductsWrapper;
